import i18n from 'i18n'
import * as Sentry from '@sentry/react'
import { isPlainObject } from 'lodash'

import setupProcessApi from 'domain/campaignSetup/api'
import { resolveConversionTrackingFields } from 'domain/campaignSetup/mappers'
import integrationAssetsApi from 'domain/integrationAssets/api'
import publishedCampaignSetupsApi from 'domain/publishedCampaignSetups/api'
import { buildSetupPayload, CampaignGoal, getLocaleForLanguage } from 'api/models'
import { userReportsApi } from 'api/userReports'
import { codifyAPIError } from 'utils/helpers'
import { actions } from './actionTypes'
import {
  buildTargetingPayload,
  extractCampaignWideTargeting,
  stripEmptyPlatformTargeting,
} from './util'
import {
  activeTargetingPlatforms,
  gatedTargetingPlatforms,
  pickNonGeoTargetingFields,
} from 'components/Recommendations/Campaign/Edit/CampaignForm/Targeting/targeting'
import {
  derivePlatformSelectionFromSetup,
  platformIncludesMeta,
} from 'components/Recommendations/Campaign/platform'

export const asyncActionHandlers = {
  [actions.syncAsync]: syncSetupAsync,
  [actions.fetchAsync]: fetchSetupAsync,
  [actions.createAsync]: createSetupAsync,
  [actions.reconfigureAsync]: reconfigureSetupAsync,
  [actions.deleteAsync]: deleteSetupAsync,
  [actions.updateTCTargetingAsync]: updateTCTargetingAsync,
  [actions.publicAsync]: publishSetupAsync,
  [actions.editAsync]: editPublishedSetupAsync,
  [actions.fetchAccountsAsync]: fetchAccountsAsync,
  [actions.validateAccountsAsync]: enrichAccountsAsync,
  [actions.enrichAccountsAndReconfigureAsync]: enrichAccountsAndReconfigureAsync,
}

export function fetchAccountsAsync({ dispatch, platforms }) {
  return async () => {
    dispatch({ type: actions.loading })
    const response = await integrationAssetsApi.listAssetsAsync({ platforms })
    if (!response.error) {
      // The unified endpoint (app/api/v4/integrations/connect/assets.py)
      // only supports filtering by platform_user_ids, not is_enabled, unlike
      // the old Facebook-scoped listFacebookAssetsAsync({ is_enabled: true })
      // call this replaces — filter client-side to keep the same behavior.
      const enabledAccounts = response.data.filter((x) => x.is_enabled)
      dispatch({
        type: actions.setAccounts,
        payload: enabledAccounts.map((x) => ({ ...x, valid: !!x.is_active })),
      })
      return { success: true, data: enabledAccounts }
    }

    handleError(dispatch, response)

    return { success: false }
  }
}

export function enrichAccountsAsync({ dispatch }) {
  return async (action) => {
    const { accounts } = action.payload
    dispatch({ type: actions.loading })

    const response = await integrationAssetsApi.enrichAssetsAsync(
      accounts
        // custom_event_type is a fixed vocabulary value (e.g. 'PURCHASE'),
        // not a deletable external resource — nothing to enrich, so it's
        // never sent. Everything else with an asset_id is sent regardless
        // of whether it's backed by a PartnerIntegration DB row: conversion/
        // conversion_action/lead_form entries are injected directly into
        // integration_details (no `.id`), but the backend can still validate
        // them live.
        .filter((x) => !!x.asset_id && x.asset_class !== 'custom_event_type')
        .map((x) => ({
          ...(x.id ? { id: Number(x.id) } : {}),
          asset_id: x.asset_id,
          asset_class: x.asset_class,
          platform: x.platform,
        })),
    )

    if (response.error) {
      handleError(dispatch, response)
      return { success: false }
    }

    const newAccounts = accounts.map((account) => {
      // custom_event_type is never sent for enrichment (see filter above) —
      // treat it as always valid rather than falling into the "not found in
      // response" branch below, which would otherwise force it invalid.
      if (account.asset_class === 'custom_event_type') {
        return { ...account, valid: true }
      }

      const enrichedAccount = response.data.find(
        (x) =>
          x.asset_id === account.asset_id &&
          x.asset_class === account.asset_class &&
          x.platform === account.platform,
      )
      if (!enrichedAccount) {
        return { ...account, valid: false }
      }

      return { ...account, ...enrichedAccount, valid: enrichedAccount.valid }
    })

    // `valid` can be `null` (backend couldn't run a live check — e.g. a
    // conversion_action with no sibling ad_account in this batch, or a
    // Google asset_class enrich doesn't support). Only a definitive `false`
    // counts as invalid; don't block on an inconclusive check.
    const hasInvalidAccounts = newAccounts.some((x) => x.valid === false)

    dispatch({ type: actions.patchAccounts, payload: newAccounts })
    return { success: true, data: response.data, hasInvalidAccounts }
  }
}

export function enrichAccountsAndReconfigureAsync({ dispatch, getState }) {
  return async (action) => {
    // validate accounts, upon success, and if all accounts are valid, reconfigure the setup
    const { eid, config } = action.payload
    let { integration_details: accounts } = config
    accounts = accounts || getState().setup.integration_details

    if (!accounts) {
      const reconfigureAction = { type: actions.reconfigureAsync, payload: { eid, config } }
      return reconfigureSetupAsync({ dispatch, getState })(reconfigureAction)
    }

    const validateAction = { type: actions.validateAccountsAsync, payload: { accounts } }
    const response = await enrichAccountsAsync({ dispatch })(validateAction)

    if (!response.success) {
      return { success: false }
    }

    const { hasInvalidAccounts, data: enrichedPartnerAccounts } = response

    // enrich selected accounts
    const newIntegrations = accounts.map((x) => {
      const enrichedAccount = enrichedPartnerAccounts.find(
        (y) =>
          y.asset_id === x.asset_id && y.asset_class === x.asset_class && y.platform === x.platform,
      )
      if (!enrichedAccount) {
        return x
      }

      return { ...x, ...enrichedAccount }
    })

    const reconfigureAction = {
      type: actions.reconfigureAsync,
      payload: { eid, config: { ...config, integration_details: newIntegrations } },
    }
    // Update the setup anyway, regardless of the account validation result
    // This will make sure that the state on screen matches the actual state
    const result = await reconfigureSetupAsync({ dispatch, getState })(reconfigureAction)

    if (hasInvalidAccounts) {
      dispatch({ type: actions.error, payload: 'common.genericError' })
      return { success: false, hasInvalidAccounts }
    } else {
      return result
    }
  }
}

export function deleteSetupAsync({ dispatch, getState }) {
  return async (action) => {
    const { eid, cancel } = action.payload
    const {
      setup: { id },
    } = getState()
    dispatch({ type: actions.loading })

    const response = await setupProcessApi.deleteAsync(eid, cancel)
    if (response.status === 200) {
      dispatch({ type: actions.setupLoaded, payload: null })
      await userReportsApi.deleteUserReports({ setup_process_ids: [id] })
      dispatch({ type: actions.fetchAsync, payload: { eid } })

      return { success: true }
    } else {
      handleError(dispatch, response)

      return { success: false }
    }
  }
}

export function createSetupAsync({ dispatch }) {
  return async (action) => {
    const { eid, config } = action.payload
    const defaultLanguage = i18n.language

    const payload = buildSetupPayload(
      {
        ...config,
        creatives: config.creatives.map((creative) => ({
          ...creative,
          language: creative.language || defaultLanguage,
        })),
      },
      { setupOnly: true, setDefaultTargeting: true },
    )

    // buildSetupPayload's targeting synthesis only knows the old v3 flat
    // shape and would strip fb/google keys — use the caller's already
    // v4-shaped targeting as-is, stripped of content-less platform blocks.
    if (Array.isArray(config.targeting)) {
      payload.targeting = stripEmptyPlatformTargeting(config.targeting)
    }
    if (config.platform_overrides) {
      payload.platform_overrides = config.platform_overrides
    }
    if (config.platform) {
      payload.platform = config.platform
    }

    dispatch({ type: actions.loading })

    const response = await setupProcessApi.createAsync(eid, payload)
    if (response.status === 200) {
      dispatch({ type: actions.setupLoaded, payload: response.data })

      return {
        success: true,
        data: response.data,
      }
    } else {
      handleError(dispatch, response)

      return {
        success: false,
        data: response.data,
      }
    }
  }
}

export function fetchSetupAsync({ dispatch }) {
  return async (action) => {
    const { eid } = action.payload
    dispatch({ type: actions.loading })

    const response = await setupProcessApi.fetchAsync(eid)
    if (response.status === 200) {
      dispatch({ type: actions.setupLoaded, payload: response.data })

      return {
        success: true,
        setup: response.data,
      }
    } else if (response.status === 404) {
      dispatch({ type: actions.setupNotFound })

      return {
        success: true,
        setup: null,
        notFound: true,
      }
    } else {
      handleError(dispatch, response)

      return { success: false }
    }
  }
}

export function syncSetupAsync({ dispatch, getState }) {
  return async (action) => {
    dispatch({ type: actions.loading })
    let { eid, setup } = getState()
    if (action?.payload) {
      setup = action.payload.setup
    }

    const payload = buildSetupPayload(setup)

    // buildSetupPayload's targeting synthesis only knows the old v3 flat
    // shape and would strip fb/google keys — use the already-v4-shaped
    // setup.targeting as-is, stripped of content-less platform blocks.
    payload.targeting = stripEmptyPlatformTargeting(setup.targeting)
    if (setup.platform_overrides) {
      payload.platform_overrides = setup.platform_overrides
    }
    if (setup.platform) {
      payload.platform = setup.platform
    }

    const response = await setupProcessApi.updateAsync(eid, payload)
    if (response.status === 200) {
      dispatch({ type: actions.setupSynced, payload: Date.now() })

      return {
        success: true,
        data: response.data,
      }
    }

    handleError(dispatch, response)

    return { success: false }
  }
}

export function reconfigureSetupAsync({ dispatch, getState }) {
  return async (action) => {
    const { eid, config } = action.payload
    const { setup } = getState()
    const { creatives: currentCreatives } = setup
    const goal = config.goal || setup.goal

    // config.platform (present when the platform picker itself just changed,
    // e.g. CampaignSetup.jsx's writeThroughPlatformAndCampaignType) reflects
    // the platform being switched to; other reconfigureSetup callers that
    // don't touch platform fall back to the setup's current one.
    const nextPlatform = derivePlatformSelectionFromSetup({
      platform: config.platform ?? setup.platform,
    })

    const { leadFormId, conversionId, customEventType } = resolveConversionTrackingFields({
      setup,
      config,
      isUtilizationGoal: goal === CampaignGoal.utilization,
      includesMeta: platformIncludesMeta(nextPlatform),
    })

    const language = currentCreatives.length > 0 ? currentCreatives[0].language : i18n.language

    const newCreatives = config.creatives.map((c) => {
      const currentCreative = currentCreatives.find(
        (cc) => cc.tc === c.tc && cc.tc_run_id === c.tc_run_id,
      )
      if (currentCreative) {
        return { ...currentCreative, ...c }
      }

      // a new creative
      return { ...c, language }
    })

    // config.targeting is the v4 platform-nested skeleton CampaignSetup.jsx
    // builds via buildTargetingSkeleton — already scoped to the selected TCs
    // and merged with each entry's existing fb/google detail. Callers that
    // don't build it (e.g. Accounts.jsx's account-selection step) fall back
    // to the old filtered-existing-targeting behavior.
    const targeting = Array.isArray(config.targeting)
      ? config.targeting
      : setup.targeting.filter((x) => config.creatives.some((y) => y.tc === x.tc))

    dispatch({ type: actions.loading })

    const payload = buildSetupPayload(
      {
        ...setup,
        ...config,
        creatives: newCreatives,
        targeting,
        lead_form_id: leadFormId,
        conversion_id: conversionId,
        custom_event_type: customEventType,
      },
      { setupOnly: true, setDefaultTargeting: true },
    )

    // buildSetupPayload's targeting synthesis only knows the old v3 flat
    // shape and would strip fb/google keys — use the v4-shaped `targeting`
    // computed above as-is, stripped of content-less platform blocks.
    if (Array.isArray(config.targeting)) {
      payload.targeting = stripEmptyPlatformTargeting(targeting)
    }
    if (config.platform_overrides) {
      payload.platform_overrides = config.platform_overrides
    }
    if (config.platform) {
      payload.platform = config.platform
    }
    // buildSetupPayload's destructure doesn't include campaign_targeting
    // either — same gap platform/platform_overrides/targeting above already
    // work around. Without this, writeThroughPlatformAndCampaignType's
    // stale-gated-platform clear (CampaignSetup.jsx) would build a correct
    // config.campaign_targeting and have it silently dropped here.
    if (config.campaign_targeting) {
      payload.campaign_targeting = config.campaign_targeting
    }

    const response = await setupProcessApi.updateAsync(eid, payload)
    if (response.status === 200) {
      dispatch({ type: actions.setupLoaded, payload: response.data })

      return {
        success: true,
        data: response.data,
      }
    } else {
      const error = handleError(dispatch, response)

      return {
        success: false,
        data: response.data,
        error,
      }
    }
  }
}

export function updateTCTargetingAsync({ dispatch, getState }) {
  return async (action) => {
    const { eid, setup } = getState()
    const { shouldSync, isFirstTc, ...targetingPayload } = action.payload
    const platforms = activeTargetingPlatforms(derivePlatformSelectionFromSetup(setup))
    const newTargeting = buildTargetingPayload(setup, targetingPayload, platforms)
    dispatch({ type: actions.setTargeting, payload: newTargeting })

    if (!shouldSync) {
      return { success: true }
    }

    // Google Display requires geo at campaign_targeting.google only — TC-level
    // targeting[].google.range_locations/postal_codes is rejected at publish
    // time for DISPLAY. Meta+Google combined requires the same for fb. Only
    // TC #1 is ever independently edited under either gate (see
    // gatedTargetingPlatforms), so its contribution is extracted directly —
    // no union needed. Every other platform/combination is unaffected:
    // gatedPlatforms is empty and newTargeting passes through unchanged.
    // isFirstTc is caller-supplied (see targeting.js) — never derived from
    // setup.creatives here, its order isn't a reliable identity signal.
    const gatedPlatforms = gatedTargetingPlatforms(setup)
    const { targeting: outgoingTargeting, campaignTargeting } = extractCampaignWideTargeting(
      newTargeting,
      gatedPlatforms,
      isFirstTc,
      targetingPayload.tc,
    )

    // buildSetupPayload's targeting synthesis only knows the old v3 flat
    // shape and would strip fb/google keys — use the v4-shaped targeting
    // (and its extracted campaign_targeting, if any) as-is.
    const payload = buildSetupPayload(
      { ...setup, targeting: outgoingTargeting },
      { setupOnly: true, setDefaultTargeting: true },
    )
    payload.targeting = stripEmptyPlatformTargeting(outgoingTargeting)
    if (campaignTargeting) {
      payload.campaign_targeting = { ...setup.campaign_targeting, ...campaignTargeting }
    }
    if (setup.platform_overrides) {
      payload.platform_overrides = setup.platform_overrides
    }

    const response = await setupProcessApi.updateAsync(eid, payload)
    if (response.status === 200) {
      dispatch({ type: actions.setupLoaded, payload: response.data })

      return {
        success: true,
        data: response.data,
      }
    }

    handleError(dispatch, response)

    return {
      success: false,
      data: response.data,
    }
  }
}

export function updateTargetingAsync({ dispatch, getState }) {
  return async (action) => {
    const { eid, setup } = getState()
    const { targeting, shouldSync } = action.payload
    dispatch({ type: actions.setTargeting, payload: targeting })
    if (!shouldSync) {
      return { success: true }
    }
    dispatch({ type: actions.loading })
    const payload = buildSetupPayload(
      { ...setup, targeting },
      { setupOnly: true, setDefaultTargeting: true },
    )

    // buildSetupPayload's targeting synthesis only knows the old v3 flat
    // shape and would strip fb/google keys — use the v4-shaped targeting
    // as-is, stripped of content-less platform blocks.
    payload.targeting = stripEmptyPlatformTargeting(targeting)
    if (setup.platform_overrides) {
      payload.platform_overrides = setup.platform_overrides
    }

    const response = await setupProcessApi.updateAsync(eid, payload)
    dispatch({ type: actions.loading, payload: false })
    if (response.status === 200) {
      dispatch({ type: actions.setupLoaded, payload: response.data })
      return {
        success: true,
        data: response.data,
      }
    }

    handleError(dispatch, response)
    return {
      success: false,
      data: response.data,
    }
  }
}

export function publishSetupAsync({ dispatch, getState }) {
  return async () => {
    const { eid } = getState()

    dispatch({ type: actions.loading })

    const response = await setupProcessApi.publishAsync(eid)
    if (response.status === 200) {
      dispatch({ type: actions.setupLoaded, payload: response.data })

      return { success: true }
    }

    if (response.status === 400 || response.status === 422) {
      reportSetupValidationError(response, {
        eid,
        errorType: 'campaign_publish_error',
        defaultMessage: 'Campaign publish error',
      })
    }

    handleError(dispatch, response)

    return { success: false }
  }
}

// Builds the payload for editing a single taste cluster's creative on an
// already-published campaign, fed to editPublishedTcAsync's `creative` field
// (full object required even if unchanged, per the v4 send-all-fields
// contract). Kept as a local helper (not pushed into
// domain/publishedCampaignSetups) since it's UI-state-shaping (excluded
// variations from local disabledVariations state), not wire-shape logic.
function buildEditedCreativePayload({ creative, tc, disabledVariations, language }) {
  const creatives = {
    ...creative,
    body: creative.body.filter(Boolean),
    headline: creative.headline.filter(Boolean),
    destination_url: creative.destination_url.filter(Boolean),
    default_locale: getLocaleForLanguage(creative.language),
    localizations: creative.localizations || {},
  }

  if (disabledVariations) {
    creatives.excluded_variations = disabledVariations[tc]
  }

  if (!creatives.language) {
    creatives.language = language
  }

  return creatives
}

export function editPublishedSetupAsync({ dispatch, getState }) {
  return async ({ payload: { tc, tc_run_id, disabledVariations, isFirstTc = false } }) => {
    const { eid, setup } = getState()
    dispatch({ type: actions.loading })

    const creative = setup.creatives.find((c) => c.tc === tc && c.tc_run_id === tc_run_id)
    const creatives = buildEditedCreativePayload({
      creative,
      tc,
      disabledVariations,
      language: i18n.language,
    })

    const gatedPlatforms = gatedTargetingPlatforms(setup)

    // A gated platform's GEO fields must never reach the per-TC endpoint — see
    // Context/README.md for this whole two-request contract.
    const { targeting: extractedTargeting, campaignTargeting } = extractCampaignWideTargeting(
      setup.targeting ?? [],
      gatedPlatforms,
      isFirstTc,
      tc,
    )

    // Must precede the POST: edit_campaign_setup translates against the
    // *stored* campaign_targeting.
    if (campaignTargeting) {
      const { error, status } = await publishedCampaignSetupsApi.updateCampaignTargetingAsync(
        eid,
        campaignTargeting,
      )
      if (error) {
        handleError(dispatch, { error, status })

        return { success: false }
      }
    }

    // editPublishedTcAsync's endpoint expects `targeting` either omitted or
    // platform-keyed ({fb, google}), not the flat v3 shape — setup.targeting
    // is already in that shape here.
    const tcTargetingForSave = extractedTargeting.find(
      (x) => x.tc === tc && x.tc_run_id === tc_run_id,
    )

    const [strippedTcTargeting] = tcTargetingForSave
      ? stripEmptyPlatformTargeting([tcTargetingForSave])
      : []
    const outgoing = {
      ...(strippedTcTargeting?.fb ? { fb: strippedTcTargeting.fb } : {}),
      ...(strippedTcTargeting?.google ? { google: strippedTcTargeting.google } : {}),
    }

    // edit_campaign_setup carries a platform's *previous* targeting forward
    // whenever its key is absent, so dropping an emptied key reads as "leave
    // it alone" rather than "clear it". A gated platform is geo-stripped down
    // to audience-only, so clearing that audience empties the whole block —
    // send it explicitly instead of losing the edit.
    const rawTcTargeting = setup.targeting?.find((x) => x.tc === tc && x.tc_run_id === tc_run_id)
    for (const platform of gatedPlatforms) {
      if (outgoing[platform] || !rawTcTargeting?.[platform]) continue
      const nonGeo = pickNonGeoTargetingFields(rawTcTargeting[platform])
      if (Object.keys(nonGeo).length) outgoing[platform] = nonGeo
    }

    const targeting = Object.keys(outgoing).length ? outgoing : undefined

    const { error, status } = await publishedCampaignSetupsApi.editPublishedTcAsync(eid, {
      tc,
      tcRunId: tc_run_id,
      creative: creatives,
      targeting,
    })

    if (!error) {
      // Don't null state.setup here: CampaignSetupContext's own effect
      // re-syncs it from a fresh `campaignSetup` once the caller's refetch
      // lands, but that effect only fires when the refetched content
      // actually differs from before. Saving with no real changes means the
      // refetch comes back identical, so the effect never fires — nulling
      // setup here would leave it null for good in that case instead of
      // just until the refetch arrives.
      dispatch({ type: actions.setupSynced, payload: Date.now() })

      return { success: true, tc, tc_run_id }
    }

    if (status === 400 || status === 422) {
      reportSetupValidationError(
        { error, status },
        {
          eid,
          tc,
          tc_run_id,
          errorType: 'campaign_edit_error',
          defaultMessage: 'Campaign edit error',
        },
      )
    }

    handleError(dispatch, { error, status })

    return { success: false }
  }
}

// Shared by publishSetupAsync/editPublishedSetupAsync: both report a
// validation-failure 400/422 to Sentry with the same shape (error
// message/code + the backend's structured validation errors/warnings).
function reportSetupValidationError(response, { eid, tc, tc_run_id, errorType, defaultMessage }) {
  // Domain api modules hand back the unwrapped response body; a raw AxiosError still carries it on .response.
  const errorResponse = response.error?.response?.data ?? response.error ?? {}
  const errorMessage = errorResponse.message || defaultMessage
  const errorCode = errorResponse.error_code || null
  const validation = errorResponse.validation || {}

  Sentry.captureMessage(`Campaign ${tc ? 'edit' : 'publish'} failed with 400/422`, {
    level: 'error',
    tags: {
      eventId: eid,
      statusCode: response.status,
      errorType,
      errorCode: errorCode || 'unknown',
    },
    extra: {
      eventId: eid,
      ...(tc ? { tc, tc_run_id } : {}),
      status: response.status,
      errorMessage,
      errorCode,
      validation: {
        valid: validation.valid,
        errors: validation.errors || [],
        warnings: validation.warnings || [],
      },
      // validate_setup_input's field -> message map, which isn't part of `validation`.
      errors: errorResponse.errors || null,
      fullErrorResponse: errorResponse,
    },
  })
}

function handleError(dispatch, { error, status, message, data }) {
  Sentry.captureException(error)
  console.error('Failed to save setup', error, status, message, data)

  const errorCode = codifyAPIError({ error, status, message, data })
  // The API sends a message's interpolation values (e.g. both ad accounts' timezones) as `data`.
  const values = isPlainObject(error?.data) ? error.data : null
  const payload = values ? { key: errorCode, values } : errorCode
  dispatch({ type: actions.error, payload })

  return payload
}
